/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { BusinessPartnerUser } from '@msdyn365-commerce/retail-proxy';
import { getAccessibleUsersAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnerUsersDataActions.g';

import { buildCacheKey } from './utilities/utils';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';

/**
 * Input for the get accessible business partner users data action.
 */
export class GetAccessibleBusinessPartnerUsersInput implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('AccessibleBusinessPartnerUsers', this.apiSettings);

    public getCacheObjectType = () => 'BusinessPartnerUser';

    public dataCacheType = (): CacheType => 'request';
}

/**
 * Creates the input for the get accessible business partner users data action.
 * @param inputData - Action creation context.
 * @returns The data action input.
 * @throws When the current user is not authenticated.
 */
export const createGetAccessibleBusinessPartnerUsersInput = (inputData: ICreateActionContext): IActionInput => {
    if (!inputData.requestContext.user.isAuthenticated) {
        throw new Error('User is not logged in.');
    }

    return new GetAccessibleBusinessPartnerUsersInput(inputData.requestContext.apiSettings);
};

/**
 * Gets the business partner users the current user is authorized to access (for example, across organizations).
 * @param input - Data action input.
 * @param context - Data action context.
 * @returns The accessible business partner users, or an empty list when the user is not a B2B contact.
 */
export async function getAccessibleBusinessPartnerUsers(
    input: GetAccessibleBusinessPartnerUsersInput,
    context: IActionContext
): Promise<BusinessPartnerUser[]> {
    // These business partner users are only available to B2B contacts. Skip the call for everyone else so the action
    // resolves to an empty list instead of issuing an unnecessary (and failing) request on both SSR and CSR.
    if (!context.requestContext.user.isB2bContact) {
        return [];
    }

    return getAccessibleUsersAsync({
        callerContext: context,
        queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
    });
}

export const getAccessibleBusinessPartnerUsersDataAction = createObservableDataAction({
    id: 'get-accessible-business-partner-users',
    action: <IAction<BusinessPartnerUser[]>>getAccessibleBusinessPartnerUsers,
    input: createGetAccessibleBusinessPartnerUsersInput
});

export default getAccessibleBusinessPartnerUsersDataAction;
